import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import isNil from 'lodash/isNil';

import { CLIENT_EVENT_NAME, TIME_KEYS, getClientHashKeys } from '@redux/constant';
import commonUtil from '@utils/common-util';
import { gu_flattenObject } from '@utils/general-util';

const initialState = {};

const getEventpayloadCurrentTime = (event) => {
    let jsonObject = JSON.stringify(event);
    Object.keys(TIME_KEYS).forEach((time) => {
        jsonObject = jsonObject.split(`<${time}>`).join(TIME_KEYS[time]);
    });
    return JSON.parse(jsonObject);
};

const mappingEventSportRadar = (events, isLogin) => {
    let returnEvents = '';
    events.split(',').forEach((event) => {
        if (event === CLIENT_EVENT_NAME.LOGIN) returnEvents = returnEvents.concat(`${CLIENT_EVENT_NAME.LOGIN_COMPLETE} `);
        else if (event === CLIENT_EVENT_NAME.SIGN_UP && isLogin) returnEvents = returnEvents.concat(`${CLIENT_EVENT_NAME.SIGN_UP_COMPLETE} `);
        else if (event === CLIENT_EVENT_NAME.SIGN_UP && !isLogin) returnEvents = returnEvents.concat(`${CLIENT_EVENT_NAME.SIGN_UP_START} `);
        else returnEvents = returnEvents.concat(`${event} `);
    });

    return returnEvents.trim().replace(/\s+/g, ",");
};

export const fetchPixelPostbackSettings = createAsyncThunk(
    'hybrid-event_postback-settings/fetchPixelPostbackSettings',
    async ({ eventNames = '', aff_id, login, referralSource }, { rejectWithValue }) => {
        try {
            const response = await window.SPL_Content.getHybridEventPostbackSettings();
            const eventPostbackParams = JSON.parse(window.SPL_Cookie.getCookie('EVENT_POSTBACK_PARAMS') || '{}');

            Object.keys(response).forEach((platformName) => {
                const platformData = response[platformName];
                if (Array.isArray(platformData.eventPostbackSettings)) {
                    const eventPostbackSettings = {};
                    platformData.eventPostbackSettings.forEach((event) => {
                        let params = event.params;
                        const hashKeysObj = {
                            ...getClientHashKeys({
                                aff_id,
                                login: login || eventPostbackParams?.login,
                                referralSource,
                                platformTrackingId: platformData.platformSettings[0]?.platformTrackingId,
                            }),
                            ...eventPostbackParams,
                        };

                        Object.keys(hashKeysObj).forEach((hashDataKey) => {
                            if (params.includes(`<${hashDataKey}>`)) {
                                const safeValue = JSON.stringify(`${hashKeysObj[hashDataKey] || ''}`).slice(1, -1);
                                params = params.split(`<${hashDataKey}>`).join(safeValue);
                            }
                        });

                        const { data, events, ...rest } = JSON.parse(params);

                        switch (platformName) {
                            case 'FB':
                            case 'TT':
                                data?.forEach((template) => {
                                    eventPostbackSettings[event.eventName] = {
                                        ...rest,
                                        ...Object.fromEntries(Object.entries(gu_flattenObject(template)).filter(([, value]) => !isNil(value) && value !== '')),
                                    };
                                });
                                break;
                            case 'GA4':
                                events?.forEach((template) => {
                                    eventPostbackSettings[event.eventName] = {
                                        ...rest,
                                        ...Object.fromEntries(Object.entries(gu_flattenObject(template, '', {}, false)).filter(([, value]) => !isNil(value) && value !== '')),
                                    };
                                });
                                break;
                            case 'SPR':
                                events?.forEach((template) => {
                                    template = Object.fromEntries(
                                        Object.entries(gu_flattenObject(template, '', {}, false)).filter(([, value]) => !isNil(value) && value !== '')
                                    );
                                    const eventName = `${event.eventName}_${template.action || ''}`;
                                    eventPostbackSettings[eventName] = {
                                        ...rest,
                                        ...template,
                                    };
                                });
                                if (eventNames) eventNames = mappingEventSportRadar(eventNames, !!login);
                                break;
                            default:
                                event.params = params;
                                break;
                        }
                        platformData.eventPostbackSettings = eventPostbackSettings;
                    });
                }
            });

            if (login) {
                window.SPL_Cookie.setCookie('EVENT_POSTBACK_PARAMS', JSON.stringify({ ...eventPostbackParams, login }));
            }

            return { ...response, eventPostbackParams, eventNames };
        } catch (err) {
            return rejectWithValue({});
        }
    }
);

const pixelPostbackSlice = createSlice({
    name: 'pixel-postback',
    initialState,
    reducers: {
        triggerEventPostbackByPlatform: (state, action) => {
            commonUtil.triggerPixelPostbackEvent(current(state), action.payload);
        },
        triggerEventStartRegister: (state, action) => {
            commonUtil.triggerPixelPostbackEvent(current(state), mappingEventSportRadar(action.payload, false));
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPixelPostbackSettings.fulfilled, (state, action) => {
            const { eventNames, ...res } = action.payload;
            Object.keys(res).forEach((key) => {
                state[key] = res[key];
            });

            // inject script postback
            commonUtil.initPixelPostbackEvent(res);

            eventNames.split(',').forEach((eventName) => {
                switch (eventName) {
                    case CLIENT_EVENT_NAME.LOGIN:
                    case CLIENT_EVENT_NAME.LOGIN_COMPLETE:
                    case CLIENT_EVENT_NAME.SIGN_UP:
                    case CLIENT_EVENT_NAME.SIGN_UP_COMPLETE:
                        commonUtil.triggerPixelPostbackEvent(getEventpayloadCurrentTime(res), eventName);
                        break;
                    case CLIENT_EVENT_NAME.LOGOUT:
                        commonUtil.triggerPixelPostbackEvent(getEventpayloadCurrentTime(res), CLIENT_EVENT_NAME.LOGOUT);
                        commonUtil.revokePlatformEventTrigger().then(() => {});
                        break;
                    default:
                        break;
                }
            });
        });
    },
});

export const { triggerEventPostbackByPlatform, triggerEventStartRegister } = pixelPostbackSlice.actions;

export default pixelPostbackSlice.reducer;
